import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Camera, Bell, Warning, ShieldCheck } from '@phosphor-icons/react';

const PERMISSIONS = [
  { key: 'location', label: 'Location (GPS)', icon: MapPin, desc: 'Required for live tracking' },
  { key: 'camera', label: 'Camera', icon: Camera, desc: 'Required for photo capture' },
  { key: 'notification', label: 'Notifications', icon: Bell, desc: 'Required for trip alerts' },
];

export default function PermissionGate({ children }) {
  const [permissions, setPermissions] = useState({
    location: 'checking',
    camera: 'checking',
    notification: 'checking',
  });
  const [allGranted, setAllGranted] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const checkPermissions = useCallback(async () => {
    const result = { location: 'denied', camera: 'denied', notification: 'denied' };

    // Check location
    try {
      if (navigator.permissions) {
        const geo = await navigator.permissions.query({ name: 'geolocation' });
        result.location = geo.state === 'granted' ? 'granted' : geo.state === 'prompt' ? 'prompt' : 'denied';
      } else {
        result.location = 'prompt';
      }
    } catch { result.location = 'prompt'; }

    // Check camera
    try {
      if (navigator.permissions) {
        const cam = await navigator.permissions.query({ name: 'camera' });
        result.camera = cam.state === 'granted' ? 'granted' : cam.state === 'prompt' ? 'prompt' : 'denied';
      } else {
        result.camera = 'prompt';
      }
    } catch { result.camera = 'prompt'; }

    // Check notifications
    if ('Notification' in window) {
      result.notification = Notification.permission === 'granted' ? 'granted' : Notification.permission === 'denied' ? 'denied' : 'prompt';
    } else {
      result.notification = 'granted'; // Not supported = skip
    }

    setPermissions(result);
    const granted = result.location === 'granted' && result.camera === 'granted' && result.notification === 'granted';
    setAllGranted(granted);
    return granted;
  }, []);

  useEffect(() => {
    // When running inside the Carloo native Android shell, the MainActivity
    // already requests system runtime permissions on launch + the WebView's
    // WebChromeClient auto-grants geolocation + camera to the page. We can
    // skip the in-app permission overlay, but we still trigger a single
    // getCurrentPosition() probe so the WebView surfaces the actual perm
    // state (otherwise navigator.geolocation.watchPosition can silently
    // never fire on some OEM Android builds). Phase 76g.
    const isCarlooApp = typeof navigator !== 'undefined' && /CarlooApp\//.test(navigator.userAgent || '');
    if (isCarlooApp) {
      try {
        navigator.geolocation?.getCurrentPosition(() => {}, () => {}, {
          enableHighAccuracy: true, timeout: 8000, maximumAge: 0,
        });
      } catch {}
      setPermissions({ location: 'granted', camera: 'granted', notification: 'granted' });
      setAllGranted(true);
      return;
    }
    checkPermissions();
  }, [checkPermissions]);

  const requestAllPermissions = async () => {
    setRequesting(true);

    // Request location
    if (permissions.location !== 'granted') {
      try {
        await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
        });
      } catch {}
    }

    // Request camera
    if (permissions.camera !== 'granted') {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(t => t.stop());
      } catch {}
    }

    // Request notification
    if (permissions.notification !== 'granted') {
      try {
        await Notification.requestPermission();
      } catch {}
    }

    // Re-check after requests
    await new Promise(r => setTimeout(r, 500));
    await checkPermissions();
    setRequesting(false);
  };

  if (allGranted) return children;

  // On desktop, don't block — permission gate is for mobile pilots only
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  if (!isMobile) return children;

  const getColor = (state) => {
    if (state === 'granted') return 'text-[#00C853]';
    if (state === 'denied') return 'text-[#FF3333]';
    return 'text-[#FF8C00]';
  };
  const getLabel = (state) => {
    if (state === 'granted') return 'Allowed';
    if (state === 'denied') return 'Blocked';
    return 'Required';
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6" data-testid="permission-gate">
      <div className="bg-white w-full max-w-sm p-6 space-y-6">
        <div className="text-center">
          <ShieldCheck size={48} className="mx-auto text-[#002FA7] mb-3" />
          <h2 className="text-xl font-black tracking-tight">Permissions Required</h2>
          <p className="text-sm text-[#666] mt-1">This app needs the following permissions to work properly</p>
        </div>

        <div className="space-y-3">
          {PERMISSIONS.map(p => (
            <div key={p.key} className="flex items-center gap-4 p-3 border border-[#E5E5E5]">
              <p.icon size={24} className={getColor(permissions[p.key])} />
              <div className="flex-1">
                <p className="text-sm font-medium">{p.label}</p>
                <p className="text-[10px] text-[#666]">{p.desc}</p>
              </div>
              <span className={`text-xs uppercase tracking-widest font-bold ${getColor(permissions[p.key])}`}>
                {getLabel(permissions[p.key])}
              </span>
            </div>
          ))}
        </div>

        {Object.values(permissions).includes('denied') && (
          <div className="bg-[#FF3333]/10 border border-[#FF3333]/20 p-3 flex items-start gap-2">
            <Warning size={16} className="text-[#FF3333] mt-0.5 shrink-0" />
            <p className="text-xs text-[#FF3333]">
              Some permissions are blocked. Please open your browser settings and allow Location, Camera, and Notifications for this site, then refresh.
            </p>
          </div>
        )}

        <button
          onClick={requestAllPermissions}
          disabled={requesting}
          className={`w-full py-4 text-sm font-bold uppercase tracking-widest text-white ${requesting ? 'bg-[#808080]' : 'bg-[#002FA7] hover:bg-[#002FA7]/90'}`}
          data-testid="grant-permissions-btn"
        >
          {requesting ? 'Requesting...' : 'Grant All Permissions'}
        </button>

        <p className="text-[10px] text-center text-[#666]">
          GPS tracking runs continuously in the background for trip monitoring. The app cannot function without these permissions.
        </p>
      </div>
    </div>
  );
}
